import React, { useEffect, useState } from "react";
import { Smartphone, X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "cw_a2hs_dismissed_at";
const DISMISS_DAYS = 7;

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const recentlyDismissed = () => {
  const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return t && Date.now() - t < DISMISS_DAYS * 86400000;
};

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, String(Date.now())); };
    window.addEventListener("appinstalled", onInstalled);
    if (isIos() && window.innerWidth < 1024) { setIos(true); setShow(true); }
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, String(Date.now())); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferred(null);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!show) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 z-[60] lg:left-auto lg:right-6 lg:bottom-6 lg:w-96 border border-emerald-500/40 bg-[#0A0A0A]/95 backdrop-blur-md shadow-2xl p-4" data-testid="install-prompt">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 bg-emerald-500/15 text-emerald-300 flex items-center justify-center"><Smartphone size={18}/></div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Election day ready</div>
          <div className="text-sm font-semibold mt-1">Add Collation Watch to your home screen</div>
          {ios ? (
            <div className="text-xs text-zinc-400 mt-1 leading-relaxed" data-testid="install-ios-steps">
              Tap <Share size={12} className="inline -mt-0.5"/> <span className="text-zinc-200">Share</span>, then <PlusSquare size={12} className="inline -mt-0.5"/> <span className="text-zinc-200">Add to Home Screen</span>. Opens full-screen and works offline.
            </div>
          ) : (
            <div className="text-xs text-zinc-400 mt-1">Opens full-screen like a native app and keeps working when the network drops.</div>
          )}
          <div className="mt-3 flex items-center gap-2">
            {!ios && <button onClick={install} className="px-4 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400" data-testid="install-btn">Install</button>}
            <button onClick={dismiss} className="px-3 py-2 text-xs text-zinc-400 hover:text-white" data-testid="install-dismiss-btn">{ios ? "Got it" : "Not now"}</button>
          </div>
        </div>
        <button onClick={dismiss} className="p-1 text-zinc-500 hover:text-white" aria-label="Dismiss" data-testid="install-close-btn"><X size={16}/></button>
      </div>
    </div>
  );
};
